import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { FlaskConical, Plus, Pencil, Trash2, Eye, CheckCircle, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const empty = {
  title: "", description: "", briefing: "", difficulty: "Iniciante",
  deliverables: [], skills_tested: [], points: 100, is_published: false,
};

export default function AdminPracticeLab() {
  const [projects, setProjects] = useState([]);
  const [pendingSubs, setPendingSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deliverablesInput, setDeliverablesInput] = useState("");
  const [skillsInput, setSkillsInput] = useState("");

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    const [projs, subs] = await Promise.all([
      db.entities.PracticalProject.list("-created_date"),
      db.entities.ProjectSubmission.filter({ status: "pending" }),
    ]);
    setProjects(projs);
    setPendingSubs(subs);
    setLoading(false);
  }

  function openCreate() {
    setForm(empty); setEditId(null);
    setDeliverablesInput(""); setSkillsInput("");
    setDialogOpen(true);
  }

  function openEdit(p) {
    setForm({ ...p }); setEditId(p.id);
    setDeliverablesInput((p.deliverables || []).join("\n"));
    setSkillsInput((p.skills_tested || []).join(", "));
    setDialogOpen(true);
  }

  async function handleSave() {
    const data = {
      ...form,
      deliverables: deliverablesInput.split("\n").map(s => s.trim()).filter(Boolean),
      skills_tested: skillsInput.split(",").map(s => s.trim()).filter(Boolean),
    };
    if (editId) {
      await db.entities.PracticalProject.update(editId, data);
    } else {
      await db.entities.PracticalProject.create(data);
    }
    setDialogOpen(false);
    await loadData();
  }

  async function handleDelete(id) {
    if (!window.confirm("Deletar este projeto?")) return;
    await db.entities.PracticalProject.delete(id);
    await loadData();
  }

  async function handleTogglePublish(p) {
    await db.entities.PracticalProject.update(p.id, { is_published: !p.is_published });
    await loadData();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin — Practice Lab</h1>
            <p className="text-gray-500 text-sm">Gerencie desafios e avalie entregas</p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 gap-2">
          <Plus className="w-4 h-4" /> Novo desafio
        </Button>
      </div>

      {/* Pending evaluations alert */}
      {pendingSubs.length > 0 && (
        <Card className="border-0 shadow-sm bg-amber-50 border-l-4 border-l-amber-400">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{pendingSubs.length} entrega(s) aguardando avaliação</p>
              <p className="text-amber-600 text-sm">Avalie as entregas na seção de submissões abaixo.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects list */}
      <div className="space-y-3">
        {projects.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhum desafio criado ainda.</p>
          </div>
        )}
        {projects.map(p => (
          <Card key={p.id} className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-gray-900">{p.title}</p>
                  {p.difficulty && <Badge className="border-0 bg-violet-100 text-violet-700 text-xs">{p.difficulty}</Badge>}
                  <Badge className={`border-0 text-xs ${p.is_published ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {p.is_published ? "Publicado" : "Rascunho"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mt-0.5 truncate">{p.description}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={createPageUrl("AdminProjectEvaluations") + `?project_id=${p.id}`}>
                  <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
                    <Eye className="w-3 h-3" /> Entregas
                  </Button>
                </Link>
                <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => handleTogglePublish(p)}>
                  {p.is_published ? "Despublicar" : "Publicar"}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}>
                  <Pencil className="w-4 h-4 text-gray-500" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(p.id)}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar desafio" : "Novo desafio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Título</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Nome do desafio" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Descrição curta</label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Briefing completo</label>
              <textarea
                value={form.briefing}
                onChange={e => setForm({ ...form, briefing: e.target.value })}
                rows={6}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                placeholder="Descreva o contexto, objetivos e instruções detalhadas..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Entregáveis (um por linha)</label>
              <textarea
                value={deliverablesInput}
                onChange={e => setDeliverablesInput(e.target.value)}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                placeholder="Relatório em PDF&#10;Apresentação de slides&#10;Planilha com dados"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Habilidades testadas (separadas por vírgula)</label>
              <Input value={skillsInput} onChange={e => setSkillsInput(e.target.value)} placeholder="Ex: Análise de dados, Comunicação, Excel" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Dificuldade</label>
                <select
                  value={form.difficulty}
                  onChange={e => setForm({ ...form, difficulty: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option>Iniciante</option>
                  <option>Intermediário</option>
                  <option>Avançado</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Pontos</label>
                <Input type="number" value={form.points} onChange={e => setForm({ ...form, points: Number(e.target.value) })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="pub" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} className="rounded" />
              <label htmlFor="pub" className="text-sm text-gray-700">Publicar imediatamente</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700">Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}